import useClassNames from '@medflex/hooks/useClassNames';
import Popper from 'popper.js';
import * as React from 'react';
import PopupMenuContext from './PopupMenuContext';

const usePopupMenu = (placement, onClickItem, className) => {
  const popperRef = React.useRef();
  const menuRef = React.useRef();
  const arrowRef = React.useRef();
  const backdropRef = React.useRef();
  const { triggerRef, isVisible, setIsVisible } = React.useContext(PopupMenuContext);
  const [currentPlacement, setCurrentPlacement] = React.useState(placement);
  const [popperStyle, setPopperStyle] = React.useState({});
  const [arrowStyles, setArrowStyles] = React.useState({});

  const triggerElementClickHandler = React.useCallback(
    event => {
      if (!event.defaultPrevented || event.button === 0) {
        setIsVisible(!isVisible);
        event.preventDefault();
      }
    },
    [isVisible, setIsVisible],
  );

  const itemClickHandler = (event, data) => {
    if (!event.defaultPrevented || event.button === 0) {
      event.stopPropagation();
      setIsVisible(false);
      onClickItem(data);
    }
  };

  const onClickOutside = React.useCallback(
    event => {
      if (backdropRef.current === event.target) {
        setIsVisible(false);
      }
    },
    [setIsVisible],
  );

  const destroyPopper = () => {
    if (popperRef && popperRef.current) {
      popperRef.current.destroy();
      popperRef.current = null;
    }
  };

  React.useEffect(() => {
    if (triggerRef && triggerRef.current) {
      const triggerElement = triggerRef.current;
      triggerElement.addEventListener('click', triggerElementClickHandler);
      return () => {
        if (triggerElement) {
          triggerElement.removeEventListener('click', triggerElementClickHandler);
        }
      };
    }
    return () => null;
  }, [triggerRef, placement, triggerElementClickHandler]);

  React.useEffect(() => {
    if (popperRef.current) destroyPopper();
    if (isVisible) {
      popperRef.current = new Popper(triggerRef.current, menuRef.current, {
        positionFixed: true,
        placement,
        modifiers: {
          applyStyle: { enabled: false },
          offset: { enabled: false },
          arrow: { element: arrowRef.current },
          updateStateMod: {
            enabled: true,
            order: 900,
            fn(data) {
              setPopperStyle({
                ...data.styles,
                maxWidth: document.documentElement.clientWidth,
                filter:
                  'drop-shadow(0 0 1px rgba(50,50,50,0.1)) ' +
                  'drop-shadow(0 10px 15px rgba(0,0,0,.1)) ' +
                  'drop-shadow(0 4px 6px rgba(0,0,0,.06))',
              });
              setArrowStyles(data.arrowStyles);
              setCurrentPlacement(data.placement);
              return data;
            },
          },
        },
      });
      popperRef.current.scheduleUpdate();
    }
    return () => {
      if (popperRef.current) destroyPopper();
    };
  }, [triggerRef, isVisible, placement]);

  React.useEffect(() => {
    document.addEventListener('click', onClickOutside, { capture: true });
    return () => {
      document.removeEventListener('click', onClickOutside, { capture: true });
    };
  }, [onClickOutside]);

  const cn = useClassNames(
    () => [
      'table',
      'min-w-64',
      'select-none',
      'z-popup-menu',
      currentPlacement.indexOf('top') !== -1 && 'mb-5',
      currentPlacement.indexOf('right') !== -1 && 'ml-5',
      currentPlacement.indexOf('bottom') !== -1 && 'mt-5',
      currentPlacement.indexOf('left') !== -1 && 'mr-5',
      className,
    ],
    [className, currentPlacement],
  );

  return {
    arrow: {
      style: arrowStyles,
      elRef: arrowRef,
      placement: currentPlacement,
    },
    popup: {
      style: popperStyle,
      ref: menuRef,
      className: cn,
    },
    backdrop: {
      ref: backdropRef,
    },
    isVisible,
    itemClickHandler,
  };
};

export default usePopupMenu;
